import React, { useRef, useState } from "react";

import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { setPoint, setStep } from "../../store/dashboard/slice";
import { selectDecidedCapsuleId } from "../../store/selectors/experience";
import { createQuest } from "../../store/tasks/slice";
import { isTaskCreated, selectActiveSquadId } from "../../store/user/slice";
import { sameCapsuleId } from "../../utils/capsuleId";
import { ErrorToast } from "../../utils/customToast";
import { formatDateLocal } from "../../utils/formatDateLocal";
import DatePicker from "../common/DatePicker/DatePicker";
import Icons from "../common/Icons";
import { WarningDialog } from "../common/Modals/WarningDialog";
import Select from "../common/Select";

const formatDateToMMDDYYYY = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const TagList = [
  "Research",
  "Synthesis",
  "Ideation",
  "Prototyping",
  "Testing",
  "Refinement",
  "Planning",
  "Execution",
  "Presentation",
  "Reflection",
];

/**
 * PRESENTATION ONLY. `TagList` entries are simultaneously the option `value`,
 * the option `label` and the persisted `tags` payload (brief 3.9, reproduced in
 * section 7.5), so renaming an entry would change what is stored. The array is
 * frozen, including `"Reflection"`, and only the rendered label moves.
 *
 * RED: change the value side and a submitted `tags` field stops being
 * byte-identical, which the focused test in this component's test file asserts
 * in the SAME test as the label, so a future edit cannot conflate the two.
 */
const TAG_LABEL = { Reflection: "Your Take" };

const TaskCreationForm = ({
  isOpen,
  setIsSuccessDialogOpen,
  onClose,
  setSelectedTaskDetails,
  isJourney,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { milestones } = useSelector((state) => state.dashboard);
  const { squadOnlyUsers } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.user);
  // M2: a task is written only for the active delivery context's squad.
  const activeSquadId = useSelector(selectActiveSquadId);
  const [taskName, setTaskName] = useState("");
  const [dueDate, setDueDate] = useState(null); // State for the selected due date
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false); // State to control the date-picker visibility
  const [error, setError] = useState({ taskName: "" });
  const [isCancelWarningOpen, setIsCancelWarningOpen] = useState(false);
  const descriptionRef = useRef(null);

  // The store's one answer to which level the learner is on. At a level
  // boundary the mission names neither a lesson nor a module, and the old
  // lesson-then-module read posted the task with `capsule_id` undefined.
  const capsuleId = useSelector(selectDecidedCapsuleId);

  if (!isOpen) return null;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    if (!taskName) {
      setIsLoading(false);
      setError({ taskName: "Task name is required" });
      return;
    }
    const formData = new FormData(e.target);
    const taskData = Object.fromEntries(formData.entries());
    if (!taskData.description) {
      setIsLoading(false);
      setError({ description: "Description is required" });
      descriptionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    /* NO TASK WITHOUT A LEVEL. A task is written against the decided capsule
       and that capsule's milestone; while either is unresolved (a level
       boundary the store cannot place yet, a milestone list still loading)
       the form sends nothing rather than a task with no level. */
    const milestone = (milestones || []).find((candidate) =>
      sameCapsuleId(candidate.capsule_id, capsuleId),
    );
    if (
      activeSquadId === null ||
      capsuleId === null ||
      capsuleId === undefined ||
      !milestone
    ) {
      ErrorToast("your mission isn't available right now.");
      return;
    }
    setIsLoading(true);
    const createTaskData = {
      title: taskName,
      description: taskData.description,
      due_date: dueDate ? formatDateLocal(dueDate) : null,
      user_id: parseInt(taskData.assignedTo, 10),
      creator_user_id: parseInt(taskData.assignedBy, 10),
      tags: taskData.tag,
      status_id: parseInt(taskData?.status, 10),
      milestone_id: milestone.id.toString(),
      squad_id: activeSquadId,
      capsule_id: capsuleId,
    };
    try {
      const response = await dispatch(createQuest(createTaskData)).unwrap();
      dispatch(setPoint(15));
      dispatch(setStep(4));
      dispatch(isTaskCreated(true));
      setTaskName("");
      setError({});
      if (!isJourney) {
        setSelectedTaskDetails(response);
      }
      setIsLoading(false);
      setIsSuccessDialogOpen(true);
      onClose();
    } catch (error) {
      setIsLoading(false);
      setError(error.errors);
    }
  };
  const openCancelWarning = () => setIsCancelWarningOpen(true);

  return (
    <>
      <div className="fixed inset-0 bg-darkblue bg-opacity-65 flex items-center justify-center">
        <div className="bg-background-blackpearl shadow-lg w-1/2 h-[720px] overflow-y-auto scrollbar-hidden relative">
          {/* Header */}
          <div className="sticky top-0 bg-background-blackpearl w-full flex justify-between items-center pl-6 pr-3 pt-6 pb-4 z-10">
            <div>
              <h2 className="text-sm font-normal text-[#B4C1E4] uppercase">
                NEW
              </h2>
              <div className="flex items-center p-1 gap-2 border-b border-[#F4F6FB]">
                <input
                  type="text"
                  name="taskName"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="NEW TASK NAME"
                  className="bg-transparent text-white text-xl font-light outline-none placeholder:text-white w-[150px]"
                />
                <span className="inline-block">
                  <Icons type="pen" className="w-5 h-5" color="white" />
                </span>
              </div>
              {error?.taskName && (
                <p className="text-persimmon text-xs mt-1">{error.taskName}</p>
              )}
            </div>
            <button
              onClick={() => {
                openCancelWarning();
              }}
              className="text-white hover:text-gray-300"
            >
              <Icons type="close" className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form
            className="flex flex-col pt-6 px-6 gap-y-6"
            onSubmit={handleFormSubmit}
          >
            {/* Assigned By */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs">assigned By</label>
              <Select
                name="assignedBy"
                defaultValue={user?.id.toString()}
                options={[
                  {
                    value: user?.id.toString(),
                    label: user?.first_name + " " + user?.last_name,
                  },
                ]}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            {/* Assigned To */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">
                assigned to
              </label>
              <Select
                name="assignedTo"
                defaultValue={squadOnlyUsers[0]?.id.toString()}
                options={[
                  {
                    value: user?.id.toString(),
                    label: user?.first_name + " " + user?.last_name,
                  },
                ]}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            <p className="w-full border-b border-[#B4C1E4]"></p>

            {/* Due Date */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs">due date</label>
              <div className="relative">
                <div
                  className="w-full bg-blackpearl border border-[#B4C1E4] rounded-md p-2 text-[#F4F6FB] flex items-center"
                  onClick={() => setIsDatePickerOpen(true)}
                >
                  <span>
                    {dueDate ? formatDateToMMDDYYYY(dueDate) : "mm/dd/yyyy"}
                  </span>
                  <Icons
                    type="calendar"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 cursor-pointer"
                  />
                </div>
                {isDatePickerOpen && (
                  <div className="absolute mt-2 z-50">
                    <DatePicker
                      onDateSelect={(date) => {
                        setDueDate(date);
                        setIsDatePickerOpen(false);
                      }}
                    />
                  </div>
                )}
              </div>
              {error?.due_date && (
                <p className="text-persimmon text-xs mt-1">{error.due_date}</p>
              )}
            </div>

            {/* Tag */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">tag</label>
              <Select
                name="tag"
                defaultValue={TagList[0]}
                options={TagList.map((tag) => ({
                  value: tag,
                  label: TAG_LABEL[tag] ?? tag,
                }))}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            {/* Status */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">
                status
              </label>
              <Select
                name="status"
                defaultValue={"1"}
                options={[
                  { value: "1", label: "NEW" },
                  { value: "3", label: "In Progress" },
                  { value: "5", label: "Completed" },
                ]}
                className="w-full py-2 pl-4 pr-1 bg-blackpearl"
              />
            </div>

            <p className="w-full border-b border-[#B4C1E4]"></p>

            {/* Description */}
            <div className="space-y-1">
              <label className="text-[#F4F6FB] text-xs font-medium">
                description
              </label>
              <textarea
                ref={descriptionRef}
                placeholder="Enter description of task"
                className="w-full bg-blackpearl border border-[#B4C1E4] rounded-md pl-4 py-2 text-[#F4F6FB] h-20 resize-none focus:outline-none"
                name="description"
                minLength={10}
              />
              {error?.description && (
                <p className="text-persimmon text-xs mt-1">
                  {error.description}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="sticky bottom-0 flex justify-between pt-4 bg-background-blackpearl w-full pt-4 pb-6">
              <button
                type="button"
                onClick={openCancelWarning}
                className="px-6 py-2 border border-persimmon text-persimmon rounded-full"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-persimmon text-white rounded-full"
              >
                {isLoading ? "Saving..." : "SAVE CHANGES"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <WarningDialog
        isOpen={isCancelWarningOpen}
        onClose={() => setIsCancelWarningOpen(false)}
        onProceed={onClose}
      />
    </>
  );
};

TaskCreationForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TaskCreationForm;
